import { useState } from 'react'
import { leases as leaseApi } from '@/api/endpoints'
import type { Lease, LeaseChange } from '@/api/types'
import { Err } from '@/components/Err'
import { ToneBadge } from '@/components/ToneBadge'
import { changeFailureSentence, changeText, changeWord } from '@/domain/lease'
import { usePoll } from '@/hooks/usePoll'
import { useFallback, useRefetchOn } from '@/hooks/useEventStream'
import { CHIP } from '../vm/chips'

// 내 VM 의 변경 신청 한 줄(B-27): 가장 최근 변경이 아직 살아 있거나(대기·적용 중) 방금 끝났으면(반려·실패) 그 결과를 말한다.
// 적용된 변경은 기계의 숫자 자체가 말하므로 줄이 없다. 기다리는 변경은 `취소` 로 거둔다 —
// 관리자 VM 상세가 남의 VM 에서 같은 줄을 쓸 때 그 VM 을 바꿀 수 없는 사람에게는 `cancel` 이 꺼져 칩이 없다.

const CHANGES_POLL_MS = 5000

export function ChangeLine({ lease: l, onChanged, cancel = true }: { lease: Lease; onChanged: () => void; cancel?: boolean }) {
  // 임대의 열린 변경이 바뀌면(신청·결정) 목록을 바로 다시 읽는다.
  const changes = usePoll(() => leaseApi.changes(l.id), useFallback(CHANGES_POLL_MS), [l.id, l.pending_change?.id ?? ''])
  useRefetchOn((c) => c.kind === 'lease' && c.id === l.id, changes.refresh)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const latest: LeaseChange | undefined = changes.data?.[0]
  if (!latest || latest.state === 'applied' || latest.state === 'cancelled') return null
  const withdraw = async () => {
    setBusy(true)
    setError(null)
    try {
      await leaseApi.cancelChange(l.id, latest.id)
      changes.refresh()
      onChanged()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }
  const tone = latest.state === 'rejected' || latest.state === 'failed' ? 'danger' : 'warn'
  const detail =
    latest.state === 'rejected'
      ? latest.rejected_reason
      : latest.state === 'failed'
        ? changeFailureSentence(latest.failure_reason)
        : changeText(latest, l.gpus)
  return (
    // 뒤에 오는 요소와는 다른 요소들과 같은 8px 을 띄우고, 마지막이면 띄우지 않는다.
    <div className="mb-2 flex flex-col gap-1 last:mb-0">
      <div className="flex items-center gap-2 text-[12px]">
        <ToneBadge tone={tone}>{changeWord(latest.state)}</ToneBadge>
        <span className="text-muted-foreground">{detail}</span>
        {latest.state === 'pending_approval' && cancel ? (
          <button type="button" className={CHIP} disabled={busy} onClick={() => void withdraw()}>
            {busy ? '취소 중' : '취소'}
          </button>
        ) : null}
      </div>
      <Err error={error} />
    </div>
  )
}
